import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { API } from "../../../constants/API";
import Loading from "../../../common-components/Loader";
import { OptionType } from "../../TMM/commonInterfaceTypes";
import { formatCurrency, formatCurrencyOne } from "../../../common-components/commonFunctions";

interface AccountSalesProps {
  selectedIsbn?: string | null;
  selectedIsbnOption?: OptionType | null;
  selectedTitleOption?: OptionType | null;
  DetailData?: any;
  selectedYear?: string | null;
  selectedMonth?: string | null;
}

type ViewMode = "account" | "organization";

interface AccountGroupRow {
  Group_Name: string | null;
  Display_Name: string | null;
  View_Type: "org" | "account" | "store" | "organization";
  Gross_Sales_Units: number;
  Gross_Sales_Amount: number;
  Returns_Units: number;
  Returns_Amount: number;
  Net_Sales_Units: number;
  Net_Sales_Amount: number;
  Non_Bill_Units: number;
  Non_Bill_Amount: number;
  ACCOUNT_NBR?: string | null;
  STORE_NBR?: string | null;
  ACCOUNT_NAME?: string | null;
  STORE_NAME?: string | null;
  children?: AccountGroupRow[];
}

interface DistributionRow {
  DISTRIBUTION_CHANNEL_NAME: string | null;
  Gross_Sales_Units: number;
  Gross_Sales_Amount: number;
  Returns_Units: number;
  Returns_Amount: number;
  Net_Sales_Units: number;
  Net_Sales_Amount: number;
  Non_Bill_Units: number;
  Non_Bill_Amount: number;
}

interface RegionRow {
  SALES_REGION: string | null;
  Gross_Sales_Units: number;
  Gross_Sales_Amount: number;
  Returns_Units: number;
  Returns_Amount: number;
  Net_Sales_Units: number;
  Net_Sales_Amount: number;
  Non_Bill_Units: number;
  Non_Bill_Amount: number;
}

// Helper: extract ISBN from various sources
const extractIsbn = (
  selectedIsbn: string | null | undefined,
  selectedIsbnOption: OptionType | null | undefined,
  detailData: any | null | undefined
): string | null => {
  if (selectedIsbn && selectedIsbn.trim().length > 0) {
    return selectedIsbn.trim();
  }

  if (
    selectedIsbnOption?.value &&
    typeof selectedIsbnOption.value === "string" &&
    selectedIsbnOption.value.trim().length > 0
  ) {
    return selectedIsbnOption.value.trim();
  }

  if (
    detailData?.EAN &&
    typeof detailData.EAN === "string" &&
    detailData.EAN.trim().length > 0
  ) {
    return detailData.EAN.trim();
  }

  return null;
};

const AccountSales: React.FC<AccountSalesProps> = ({
  selectedIsbn,
  selectedIsbnOption,
  selectedTitleOption,
  DetailData,
  selectedYear,
  selectedMonth,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("account");
  const [data, setData] = useState<AccountGroupRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [distributionData, setDistributionData] = useState<DistributionRow[]>([]);
  const [regionData, setRegionData] = useState<RegionRow[]>([]);

  const isbnToUse = useMemo(
    () => extractIsbn(selectedIsbn || null, selectedIsbnOption || null, DetailData),
    [selectedIsbn, selectedIsbnOption, DetailData]
  );

  // Helpers for exporting tables
  const exportToExcel = (
    fileName: string,
    rows: any[],
    numericColumns: string[] = [],
    numberFormat: string = "#,##0;(#,##0)"
  ) => {
    if (!rows || rows.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Apply Excel number format so negative numeric values show in parentheses
    // and thousands use comma separators (US format)
    if (rows.length > 0 && numericColumns.length > 0 && worksheet["!ref"]) {
      const headerKeys = Object.keys(rows[0]);

      const numericColIndexes = numericColumns
        .map((name) => headerKeys.indexOf(name))
        .filter((idx) => idx !== -1);

      if (numericColIndexes.length > 0) {
        const range = XLSX.utils.decode_range(worksheet["!ref"] as string);

        // Start from row after header (header is first row)
        for (let r = range.s.r + 1; r <= range.e.r; r++) {
          numericColIndexes.forEach((c) => {
            const cellRef = XLSX.utils.encode_cell({ r, c });
            const cell = worksheet[cellRef];

            if (cell && typeof cell.v === "number") {
              cell.t = "n";
              // Show negatives in parentheses, positives as normal numbers,
              // with comma group separators (US style)
              cell.z = numberFormat;
            }
          });
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, fileName);
  };

  const buildAccountExportRows = (): any[] => {
    const rows: any[] = [];

    const pushRow = (label: string, node: AccountGroupRow) => {
      rows.push({
        "Account Group": label,
        "Gross Units": Math.round(node.Gross_Sales_Units || 0),
        "Gross Dollars": Math.round(node.Gross_Sales_Amount || 0),
        "Return Units": Math.round(node.Returns_Units || 0),
        "Return Dollars": Math.round(node.Returns_Amount || 0),
        "Net Units": Math.round(node.Net_Sales_Units || 0),
        "Net Dollars": Math.round(node.Net_Sales_Amount || 0),
        "Non Bill Units": Math.round(node.Non_Bill_Units || 0),
        "Non Bill Dollars": Math.round(node.Non_Bill_Amount || 0),
      });
    };

    // Match on-screen sorting: orgs, accounts, and stores by Gross Dollars (descending)
    const sortedOrgs = [...data].sort(
      (a, b) => (b.Gross_Sales_Amount || 0) - (a.Gross_Sales_Amount || 0)
    );

    sortedOrgs.forEach((orgRow) => {
      const orgLabel = orgRow.Display_Name || orgRow.Group_Name || "";
      pushRow(orgLabel, orgRow);

      // In account view include account and store detail lines
      if (viewMode === "account" && orgRow.children && orgRow.children.length) {
        const sortedAccounts = [...orgRow.children].sort(
          (a, b) => (b.Gross_Sales_Amount || 0) - (a.Gross_Sales_Amount || 0)
        );

        sortedAccounts.forEach((accountRow) => {
          const accLabel = accountRow.Display_Name || "";
          pushRow(`  ${accLabel}`, accountRow);

          if (accountRow.children && accountRow.children.length) {
            const sortedStores = [...accountRow.children].sort(
              (a, b) => (b.Gross_Sales_Amount || 0) - (a.Gross_Sales_Amount || 0)
            );

            sortedStores.forEach((storeRow) => {
              const storeLabel = storeRow.Display_Name || "";
              pushRow(`    ${storeLabel}`, storeRow);
            });
          }
        });
      }
    });

    return rows;
  };

  const handleExportAccountGroup = () => {
    const rows = buildAccountExportRows();
    exportToExcel("account_sales_by_account_group.xlsx", rows, [
      "Gross Units",
      "Gross Dollars",
      "Return Units",
      "Return Dollars",
      "Net Units",
      "Net Dollars",
      "Non Bill Units",
      "Non Bill Dollars",
    ]);
  };

  const handleExportDistribution = () => {
    const rows = [...distributionData]
      // Sort by Gross Dollars descending to match table view
      .sort(
        (a, b) => (b.Gross_Sales_Amount || 0) - (a.Gross_Sales_Amount || 0)
      )
      .map((row) => ({
      "Distribution Channel": row.DISTRIBUTION_CHANNEL_NAME || "",
      "Gross Units": Math.round(row.Gross_Sales_Units || 0),
      "Gross Dollars": Math.round(row.Gross_Sales_Amount || 0),
      "Return Units": Math.round(row.Returns_Units || 0),
      "Return Dollars": Math.round(row.Returns_Amount || 0),
      "Net Units": Math.round(row.Net_Sales_Units || 0),
      "Net Dollars": Math.round(row.Net_Sales_Amount || 0),
      "Non Bill Units": Math.round(row.Non_Bill_Units || 0),
      "Non Bill Dollars": Math.round(row.Non_Bill_Amount || 0),
    }));
    exportToExcel("account_sales_by_distribution_channel.xlsx", rows, [
      "Gross Units",
      "Gross Dollars",
      "Return Units",
      "Return Dollars",
      "Net Units",
      "Net Dollars",
      "Non Bill Units",
      "Non Bill Dollars",
    ]);
  };

  const handleExportRegion = () => {
    const rows = [...regionData]
      // Sort by Gross Dollars descending to match table view
      .sort(
        (a, b) => (b.Gross_Sales_Amount || 0) - (a.Gross_Sales_Amount || 0)
      )
      .map((row) => ({
      "Sales Region": row.SALES_REGION || "",
      "Gross Units": Math.round(row.Gross_Sales_Units || 0),
      "Gross Dollars": Math.round(row.Gross_Sales_Amount || 0),
      "Return Units": Math.round(row.Returns_Units || 0),
      "Return Dollars": Math.round(row.Returns_Amount || 0),
      "Net Units": Math.round(row.Net_Sales_Units || 0),
      "Net Dollars": Math.round(row.Net_Sales_Amount || 0),
      "Non Bill Units": Math.round(row.Non_Bill_Units || 0),
      "Non Bill Dollars": Math.round(row.Non_Bill_Amount || 0),
    }));
    exportToExcel("account_sales_by_sales_region.xlsx", rows, [
      "Gross Units",
      "Gross Dollars",
      "Return Units",
      "Return Dollars",
      "Net Units",
      "Net Dollars",
      "Non Bill Units",
      "Non Bill Dollars",
    ]);
  };

  // Fetch account/organization data whenever filters change
  useEffect(() => {
    if (!isbnToUse) {
      setData([]);
      setDistributionData([]);
      setRegionData([]);
      return;
    }
    
    const fetchData = async () => {
      try {
      setLoading(true);
      setError(null);
      
        const payload: any = {
          SelectedEbookIsbn: isbnToUse,
          viewMode,
        };

        if (selectedYear) {
          payload.selectedYear = selectedYear;
        }

        if (selectedMonth) {
          payload.selectedMonth = selectedMonth;
        }

        const [accountRes, distRes, regionRes] = await Promise.all([
          axios.post(API.FETCHACCOUNTGROUPSALES, payload),
          axios.post(API.FETCH_DISTRIBUTION_SALES, {
            SelectedEbookIsbn: isbnToUse,
            selectedYear,
            selectedMonth,
          }),
          axios.post(API.FETCH_REGION_SALES, {
            SelectedEbookIsbn: isbnToUse,
            selectedYear,
            selectedMonth,
          }),
        ]);

        const rows: AccountGroupRow[] = accountRes.data?.data || [];
        setData(rows);

        const distRows: DistributionRow[] =
          distRes.data?.data || distRes.data || [];
        const regionRows: RegionRow[] =
          regionRes.data?.data || regionRes.data || [];

        setDistributionData(distRows);
        setRegionData(regionRows);
      } catch (err: any) {
        console.error("Error fetching account group sales data:", err);
        setError(
          err?.response?.data?.error ||
            "Unable to load account sales data. Please try again."
        );
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isbnToUse, selectedYear, selectedMonth, viewMode]);

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const renderHeader = (label: string = "Account Group") => (
    <thead className="bg-white">
      <tr>
        <th className="text-center border-2 border-gray-400 px-2 py-1 w-[200px]" rowSpan={2}>
          {label}
        </th>
        <th className="text-center border-2 border-gray-400 px-2 py-1" colSpan={2}>
          Gross Sales
        </th>
        <th className="text-center border-2 border-gray-400 px-2 py-1" colSpan={2}>
          Returns
        </th>
        <th className="text-center border-2 border-gray-400 px-2 py-1" colSpan={2}>
          Net Sales
        </th>
        <th className="text-center border-2 border-gray-400 px-2 py-1" colSpan={2}>
          Non - Bill
        </th>
      </tr>
      <tr>
        <th className="border-2 border-gray-400 px-2 py-1">Units</th>
        <th className="border-2 border-gray-400 px-2 py-1">$</th>
        <th className="border-2 border-gray-400 px-2 py-1">Units</th>
        <th className="border-2 border-gray-400 px-2 py-1">$</th>
        <th className="border-2 border-gray-400 px-2 py-1">Units</th>
        <th className="border-2 border-gray-400 px-2 py-1">$</th>
        <th className="border-2 border-gray-400 px-2 py-1">Units</th>
        <th className="border-2 border-gray-400 px-2 py-1">$</th>
      </tr>
    </thead>
  );

  const renderAccountRows = () => {
    // Sort organizations by Gross Sales Dollars (descending)
    const sortedOrgs = [...data].sort(
      (a, b) => (b.Gross_Sales_Amount || 0) - (a.Gross_Sales_Amount || 0)
    );

    return (
      <tbody>
        {sortedOrgs.map((orgRow, orgIndex) => {
        const orgKey = `org-${orgRow.Group_Name || orgIndex}`;
        const orgExpanded = expandedKeys.includes(orgKey);

        // Sort accounts under each org by Gross Sales Dollars (descending)
        const sortedAccounts =
          orgRow.children && orgRow.children.length
            ? [...orgRow.children].sort(
                (a, b) =>
                  (b.Gross_Sales_Amount || 0) - (a.Gross_Sales_Amount || 0)
              )
            : [];

        return (
          <React.Fragment key={orgKey}>
            {/* Organization row */}
            <tr
              className="bg-gray-100 font-semibold cursor-pointer"
              onClick={() => toggleExpand(orgKey)}
            >
              <td className="border border-gray-400 px-2 py-1">
                <div className="flex items-center justify-between">
                  <span>{orgRow.Display_Name || orgRow.Group_Name || "N/A"}</span>
                  {orgRow.children && orgRow.children.length > 0 && (
                    <span className="text-xs text-gray-600 ml-2">
                      {orgExpanded ? "−" : "+"}
                    </span>
                  )}
                </div>
              </td>
              <td className="border border-gray-400 px-2 py-1 text-right">
                {formatCurrencyOne(Math.round(orgRow.Gross_Sales_Units || 0))}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-right">
                {formatCurrency(Math.round(orgRow.Gross_Sales_Amount || 0))}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-right">
                {formatCurrencyOne(Math.round(orgRow.Returns_Units || 0))}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-right">
                {formatCurrency(Math.round(orgRow.Returns_Amount || 0))}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-right">
                {formatCurrencyOne(Math.round(orgRow.Net_Sales_Units || 0))}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-right">
                {formatCurrency(Math.round(orgRow.Net_Sales_Amount || 0))}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-right">
                {formatCurrencyOne(Math.round(orgRow.Non_Bill_Units || 0))}
              </td>
              <td className="border border-gray-400 p-2 text-right">
                {formatCurrency(Math.round(orgRow.Non_Bill_Amount || 0))}
              </td>
            </tr>

            {/* Account rows under org */}
            {orgExpanded &&
              sortedAccounts.map((accountRow, accIndex) => {
                const accKey = `acc-${orgRow.Group_Name || orgIndex}-${
                  accountRow.ACCOUNT_NBR || accIndex
                }`;
                const accExpanded = expandedKeys.includes(accKey);

                return (
                  <React.Fragment key={accKey}>
                    <tr
                      className="cursor-pointer bg-gray-50"
                      onClick={() => toggleExpand(accKey)}
                    >
                      <td className="border border-gray-400 px-2 py-1 pl-6">
                        <div className="flex items-center justify-between">
                          <span>{accountRow.Display_Name || "N/A"}</span>
                          {accountRow.children && accountRow.children.length > 0 && (
                            <span className="text-xs text-gray-600 ml-2">
                              {accExpanded ? "−" : "+"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-right">
                        {formatCurrencyOne(
                          Math.round(accountRow.Gross_Sales_Units || 0)
                        )}
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-right">
                        {formatCurrency(
                          Math.round(accountRow.Gross_Sales_Amount || 0)
                        )}
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-right">
                        {formatCurrencyOne(
                          Math.round(accountRow.Returns_Units || 0)
                        )}
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-right">
                        {formatCurrency(
                          Math.round(accountRow.Returns_Amount || 0)
                        )}
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-right">
                        {formatCurrencyOne(
                          Math.round(accountRow.Net_Sales_Units || 0)
                        )}
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-right">
                        {formatCurrency(
                          Math.round(accountRow.Net_Sales_Amount || 0)
                        )}
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-right">
                        {formatCurrencyOne(
                          Math.round(accountRow.Non_Bill_Units || 0)
                        )}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-right">
                        {formatCurrency(
                          Math.round(accountRow.Non_Bill_Amount || 0)
                        )}
                      </td>
                    </tr>

                    {/* Store rows under account */}
                    {accExpanded &&
                      accountRow.children &&
                      [...accountRow.children]
                        .sort(
                          (a, b) =>
                            (b.Gross_Sales_Amount || 0) -
                            (a.Gross_Sales_Amount || 0)
                        )
                        .map((storeRow, storeIndex) => (
                        <tr
                          key={`${accKey}-store-${storeIndex}`}
                        >
                          <td className="border border-gray-400 px-2 py-1 pl-10">
                            {storeRow.Display_Name || "N/A"}
                          </td>
                          <td className="border border-gray-400 px-2 py-1 text-right">
                            {formatCurrencyOne(
                              Math.round(storeRow.Gross_Sales_Units || 0)
                            )}
                          </td>
                          <td className="border border-gray-400 px-2 py-1 text-right">
                            {formatCurrency(
                              Math.round(storeRow.Gross_Sales_Amount || 0)
                            )}
                          </td>
                          <td className="border border-gray-400 px-2 py-1 text-right">
                            {formatCurrencyOne(
                              Math.round(storeRow.Returns_Units || 0)
                            )}
                          </td>
                          <td className="border border-gray-400 px-2 py-1 text-right">
                            {formatCurrency(
                              Math.round(storeRow.Returns_Amount || 0)
                            )}
                          </td>
                          <td className="border border-gray-400 px-2 py-1 text-right">
                            {formatCurrencyOne(
                              Math.round(storeRow.Net_Sales_Units || 0)
                            )}
                          </td>
                          <td className="border border-gray-400 px-2 py-1 text-right">
                            {formatCurrency(
                              Math.round(storeRow.Net_Sales_Amount || 0)
                            )}
                          </td>
                          <td className="border border-gray-400 px-2 py-1 text-right">
                            {formatCurrencyOne(
                              Math.round(storeRow.Non_Bill_Units || 0)
                            )}
                          </td>
                          <td className="border border-gray-400 px-2 py-1 text-right">
                            {formatCurrency(
                              Math.round(storeRow.Non_Bill_Amount || 0)
                            )}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
          </React.Fragment>
        );
      })}
    </tbody>
    );
  };

  const renderOrganizationRows = () => (
    <tbody>
      {[...data]
        // Sort organizations by Gross Sales Dollars (descending)
        .sort(
          (a, b) => (b.Gross_Sales_Amount || 0) - (a.Gross_Sales_Amount || 0)
        )
        .map((row, index) => (
        <tr key={row.Group_Name || index}>
          <td className="border border-gray-400 px-2 py-1">
            {row.Display_Name || row.Group_Name || "N/A"}
          </td>
          <td className="border border-gray-400 px-2 py-1 text-right">
            {formatCurrencyOne(Math.round(row.Gross_Sales_Units || 0))}
          </td>
          <td className="border border-gray-400 px-2 py-1 text-right">
            {formatCurrency(Math.round(row.Gross_Sales_Amount || 0))}
          </td>
          <td className="border border-gray-400 px-2 py-1 text-right">
            {formatCurrencyOne(Math.round(row.Returns_Units || 0))}
          </td>
          <td className="border border-gray-400 px-2 py-1 text-right">
            {formatCurrency(Math.round(row.Returns_Amount || 0))}
          </td>
          <td className="border border-gray-400 px-2 py-1 text-right">
            {formatCurrencyOne(Math.round(row.Net_Sales_Units || 0))}
          </td>
          <td className="border border-gray-400 px-2 py-1 text-right">
            {formatCurrency(Math.round(row.Net_Sales_Amount || 0))}
          </td>
          <td className="border border-gray-400 px-2 py-1 text-right">
            {formatCurrencyOne(Math.round(row.Non_Bill_Units || 0))}
          </td>
          <td className="border border-gray-400 px-2 py-1 text-right">
            {formatCurrency(Math.round(row.Non_Bill_Amount || 0))}
          </td>
        </tr>
      ))}
    </tbody>
  );

  const renderDistributionTable = () => (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2 px-2">
        <div className="text-sm font-semibold text-gray-800">
          By Distribution Channel
        </div>
        <button
          type="button"
          onClick={handleExportDistribution}
          className="text-xs px-3 py-1 border border-gray-400 rounded bg-white hover:bg-gray-100"
        >
          Export
        </button>
      </div>
      <div className="border-2 border-gray-300 rounded bg-white shadow-sm">
        <div className="max-h-72 overflow-y-auto pb-1">
          <table className="border-collapse w-full text-xs sm:text-sm">
            {renderHeader("Distribution Channel")}
            <tbody>
              {[...distributionData]
                // Sort distribution rows by Gross Sales Dollars (descending)
                .sort(
                  (a, b) =>
                    (b.Gross_Sales_Amount || 0) - (a.Gross_Sales_Amount || 0)
                )
                .map((row, index) => (
                <tr key={row.DISTRIBUTION_CHANNEL_NAME || index}>
                  <td className="border border-gray-200 px-2 py-1">
                    {row.DISTRIBUTION_CHANNEL_NAME || "N/A"}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrencyOne(Math.round(row.Gross_Sales_Units || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrency(Math.round(row.Gross_Sales_Amount || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrencyOne(Math.round(row.Returns_Units || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrency(Math.round(row.Returns_Amount || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrencyOne(Math.round(row.Net_Sales_Units || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrency(Math.round(row.Net_Sales_Amount || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrencyOne(Math.round(row.Non_Bill_Units || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrency(Math.round(row.Non_Bill_Amount || 0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderRegionTable = () => (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2 px-2">
        <div className="text-sm font-semibold text-gray-800">
          By Sales Region
        </div>
        <button
          type="button"
          onClick={handleExportRegion}
          className="text-xs px-3 py-1 border border-gray-400 rounded bg-white hover:bg-gray-100"
        >
          Export
        </button>
      </div>
      <div className="border-2 border-gray-300 rounded bg-white shadow-sm">
        <div className="max-h-56 overflow-y-auto pb-1">
          <table className="border-collapse w-full text-xs sm:text-sm">
            {renderHeader("Sales Region")}
            <tbody>
              {[...regionData]
                // Sort regions by Gross Sales Dollars (descending)
                .sort(
                  (a, b) =>
                    (b.Gross_Sales_Amount || 0) - (a.Gross_Sales_Amount || 0)
                )
                .map((row, index) => (
                <tr key={row.SALES_REGION || index}>
                  <td className="border border-gray-200 px-2 py-1">
                    {row.SALES_REGION || "N/A"}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrencyOne(Math.round(row.Gross_Sales_Units || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrency(Math.round(row.Gross_Sales_Amount || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrencyOne(Math.round(row.Returns_Units || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrency(Math.round(row.Returns_Amount || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrencyOne(Math.round(row.Net_Sales_Units || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrency(Math.round(row.Net_Sales_Amount || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrencyOne(Math.round(row.Non_Bill_Units || 0))}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 text-right">
                    {formatCurrency(Math.round(row.Non_Bill_Amount || 0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm sm:text-base font-semibold text-gray-800">
          Account Sales
        </div>
        <div className="flex items-center gap-1 text-xs sm:text-sm">
              <button
            type="button"
            onClick={() => setViewMode("account")}
            className={`px-3 py-1 rounded-l border border-gray-400 ${
              viewMode === "account"
                ? "bg-red-600 text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Account
              </button>
          <button
            type="button"
            onClick={() => setViewMode("organization")}
            className={`px-3 py-1 rounded-r border border-gray-400 border-l-0 ${
              viewMode === "organization"
                ? "bg-red-600 text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Organization
          </button>
        </div>
      </div>

      <div className="border-2 border-gray-300 rounded bg-white shadow-sm">
        <Loading isLoading={loading}>
          {error ? (
            <div className="py-4 text-center text-sm text-red-600">
              {error}
            </div>
          ) : data.length === 0 ? (
            <div className="py-4 text-center text-sm text-gray-600">
              No data found
            </div>
          ) : (
            <div className="space-y-6">
              {/* By Account Group section with internal scroll */}
              <div>
                <div className="flex items-center justify-between mb-2 mt-2 px-2">
                  <div className="text-sm font-semibold text-gray-800">
                    By Account Group
                  </div>
                  <button
                    type="button"
                    onClick={handleExportAccountGroup}
                    className="text-xs px-3 py-1 border border-gray-400 rounded bg-white hover:bg-gray-100"
                  >
                    Export
                  </button>
                </div>
                <div className="border-2 border-gray-300 rounded bg-white">
                  <div className="max-h-72 overflow-y-auto pb-1">
                    <table className="border-collapse w-full text-xs sm:text-sm">
                      {renderHeader("Account Group")}
                      {viewMode === "account"
                        ? renderAccountRows()
                        : renderOrganizationRows()}
                    </table>
                  </div>
                </div>
              </div>

              {/* Static sections – same layout for both Account / Organization */}
              {distributionData.length > 0 && renderDistributionTable()}
              {regionData.length > 0 && renderRegionTable()}
          </div>
        )}
      </Loading>
      </div>
    </div>
  );
};

export default AccountSales;
